import React, { useState } from 'react'
import { ChevronDownIcon, ChevronRightIcon, ShieldIcon, ClockIcon, MapPinIcon, UserIcon, CalendarIcon } from 'lucide-react'

interface AccommodationData {
  category?: string
  subcategory?: string
  description: string
  frequency?: string
  settings?: string[]
  responsible_staff?: string
  review_date?: string
  source_page?: number
}

interface AccommodationRendererProps {
  accommodations: AccommodationData[]
  onSourceClick?: (pageNumber: number) => void
}

const AccommodationRenderer: React.FC<AccommodationRendererProps> = ({
  accommodations,
  onSourceClick
}) => {
  const [expandedAccommodations, setExpandedAccommodations] = useState<Set<number>>(new Set())

  const toggleAccommodation = (index: number) => {
    const newExpanded = new Set(expandedAccommodations)
    if (newExpanded.has(index)) {
      newExpanded.delete(index)
    } else {
      newExpanded.add(index)
    }
    setExpandedAccommodations(newExpanded)
  }

  const getCategoryColor = (category?: string): string => {
    if (!category) return 'bg-gray-100 text-gray-700'
    
    const lowerCategory = category.toLowerCase()
    if (lowerCategory.includes('testing')) return 'bg-blue-100 text-blue-800'
    if (lowerCategory.includes('instruction')) return 'bg-green-100 text-green-800'
    if (lowerCategory.includes('environment')) return 'bg-purple-100 text-purple-800'
    if (lowerCategory.includes('behavior')) return 'bg-orange-100 text-orange-800'
    if (lowerCategory.includes('technology')) return 'bg-indigo-100 text-indigo-800'
    
    return 'bg-gray-100 text-gray-700'
  }

  const renderSourceReference = (pageNumber: number) => {
    if (!onSourceClick) return null
    return (
      <button
        onClick={(e) => {
          e.stopPropagation()
          onSourceClick(pageNumber)
        }}
        className="ml-2 p-1 rounded hover:bg-gray-100 transition-colors text-gray-400 hover:text-blue-600"
        title={`Go to page ${pageNumber} in PDF`}
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
        </svg>
      </button>
    )
  }

  const getShortDescription = (description: string): string => {
    // Extract the first meaningful part of the description
    if (description.length <= 60) return description
    
    // Try to break at a natural point
    const sentences = description.split('. ')
    if (sentences[0].length <= 60) return sentences[0]
    
    // Fall back to truncation at word boundary
    const words = description.split(' ')
    let short = words[0]
    for (let i = 1; i < words.length && (short + ' ' + words[i]).length <= 60; i++) {
      short += ' ' + words[i]
    }
    return short + (short !== description ? '...' : '')
  }

  if (accommodations.length === 0) {
    return (
      <div className="text-gray-500 italic p-4 bg-gray-50 rounded-lg">
        No accommodations found
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {accommodations.map((accommodation, index) => {
        const isExpanded = expandedAccommodations.has(index)
        const shortDescription = getShortDescription(accommodation.description)
        
        return (
          <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            {/* Header - Always Visible */}
            <div 
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleAccommodation(index)}
            >
              <div className="flex-1 min-w-0">
                {/* Category and Description */}
                <div className="flex items-start mb-3">
                  <div className="flex items-center mr-4">
                    {accommodation.category && (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(accommodation.category)}`}>
                        <ShieldIcon className="h-3 w-3 mr-1" />
                        {accommodation.category}
                      </span>
                    )}
                    {accommodation.subcategory && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                        {accommodation.subcategory}
                      </span>
                    )}
                    {accommodation.source_page && renderSourceReference(accommodation.source_page)}
                  </div>
                </div>
                
                <div className="mb-3">
                  <p className="text-gray-900 font-medium">{shortDescription}</p>
                </div>
                
                {/* Key Details Row */}
                <div className="flex items-center text-sm text-gray-600 space-x-6">
                  {accommodation.frequency && (
                    <div className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      <span>{accommodation.frequency}</span>
                    </div>
                  )}
                  
                  {accommodation.settings && accommodation.settings.length > 0 && (
                    <div className="flex items-center">
                      <MapPinIcon className="h-4 w-4 mr-1" />
                      <span>{accommodation.settings.slice(0, 2).join(', ')}{accommodation.settings.length > 2 ? ' +more' : ''}</span>
                    </div>
                  )}
                  
                  {accommodation.responsible_staff && (
                    <div className="flex items-center">
                      <UserIcon className="h-4 w-4 mr-1" />
                      <span>{accommodation.responsible_staff}</span>
                    </div>
                  )}
                  
                  {accommodation.review_date && (
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      <span>Review: {accommodation.review_date}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex-shrink-0 ml-4">
                {isExpanded ? (
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>
            
            {/* Expanded Details */}
            {isExpanded && (
              <div className="border-t border-gray-100 px-4 pb-4">
                <div className="pt-4 space-y-4">
                  
                  {/* Full Description */}
                  {accommodation.description !== shortDescription && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Full Description</h4>
                      <p className="text-sm text-gray-900">{accommodation.description}</p>
                    </div>
                  )}
                  
                  {/* All Settings */}
                  {accommodation.settings && accommodation.settings.length > 2 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">All Settings</h4>
                      <div className="flex flex-wrap gap-2">
                        {accommodation.settings.map((setting, idx) => (
                          <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {setting}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Implementation Details */}
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    {accommodation.responsible_staff && (
                      <div>
                        <h5 className="font-medium text-gray-700 mb-1">Responsible Staff</h5>
                        <p className="text-gray-900">{accommodation.responsible_staff}</p>
                      </div>
                    )}
                    
                    {accommodation.frequency && (
                      <div>
                        <h5 className="font-medium text-gray-700 mb-1">Frequency</h5>
                        <p className="text-gray-900">{accommodation.frequency}</p>
                      </div>
                    )}
                    
                    {accommodation.review_date && (
                      <div>
                        <h5 className="font-medium text-gray-700 mb-1">Review Date</h5>
                        <p className="text-gray-900">{accommodation.review_date}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default AccommodationRenderer
